import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";

import { captureSentryException } from "@blis/telemetry/client";

import { LoadingPage } from "@/components/LoadingPage";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { QueryProvider } from "./query";
import { ThemeProvider } from "./theme";

function ErrorFallback() {
  return (
    <div
      className="flex flex-col items-center justify-center gap-4 w-screen h-screen"
      role="alert"
    >
      <Text size="4">Ooops, something went wrong :( </Text>
      <Button
        onPress={() => window.location.assign(window.location.origin)}
        variant="outline"
      >
        Refresh
      </Button>
    </div>
  );
}

function handleBoundaryError(error: unknown, info: React.ErrorInfo) {
  // Surface render-tree crashes as Sentry Issues — without this, the only
  // signal is the white "something went wrong" screen and a console log.
  captureSentryException(error, {
    tags: { area: "react-error-boundary" },
    contexts: {
      react: { componentStack: info.componentStack ?? null },
    },
  });
}

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider = ({ children }: AppProviderProps) => {
  useTinyKeys(
    {
      Escape: (evt) => {
        (evt.target as HTMLElement).blur();
      },
    },
    [],
    false,
  );

  return (
    <ThemeProvider defaultTheme="light">
      <React.Suspense fallback={<LoadingPage />}>
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={handleBoundaryError}
        >
          <QueryProvider>{children}</QueryProvider>
        </ErrorBoundary>
      </React.Suspense>
    </ThemeProvider>
  );
};
